import { Component, Input } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { LibrarySkill, Skill, SkillService, skillErrorText } from '../../../services/skill.service';

/**
 * Import a skill: a Claude skill folder as .zip or a single SKILL.md, or a
 * link to a public GitHub repository, folder or SKILL.md. The backend parses
 * it, keeps text reference files, stores scripts as non-executable reference
 * text and creates the skill INACTIVE — the editor opens next, with the
 * import report, so it is read before it is used. A link that holds several
 * skills lists them to pick from.
 */
@Component({
  selector: 'app-skill-import-dialog',
  templateUrl: './skill-import-dialog.component.html',
  styleUrl: './skill-import-dialog.component.scss',
})
export class SkillImportDialogComponent {
  @Input() onImported: (skill: Skill) => void = () => {};
  /** after importing several from one link: back to the list */
  @Input() onImportedMany: (skills: Skill[]) => void = () => {};

  mode: 'file' | 'github' = 'file';
  url = '';
  candidates: LibrarySkill[] = [];
  selected = new Set<string>();

  file: File | null = null;
  dragging = false;
  uploading = false;
  error: string | null = null;

  constructor(
    protected dialogRef: NbDialogRef<SkillImportDialogComponent>,
    private skills: SkillService,
    private t: TranslateService
  ) {}

  pick(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.setFile(input.files?.[0] ?? null);
  }

  drop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    this.setFile(event.dataTransfer?.files?.[0] ?? null);
  }

  private setFile(file: File | null): void {
    this.error = null;
    if (file && !/\.(zip|skill|md)$/i.test(file.name)) {
      this.error = this.t.instant('pages.skills.importDialog.wrongType');
      this.file = null;
      return;
    }
    this.file = file;
  }

  onTab(event: { tabId?: string }): void {
    this.mode = event?.tabId === 'github' ? 'github' : 'file';
    this.error = null;
  }

  confirm(): void {
    if (this.mode === 'github') this.fromGithub();
    else this.upload();
  }

  toggle(path: string, on: boolean): void {
    if (on) this.selected.add(path);
    else this.selected.delete(path);
  }

  /** First call: import the one skill at the link, or list several. Second
   *  call (after picking): import the picked ones. */
  fromGithub(): void {
    const url = this.url.trim();
    if (!/^https:\/\/(github\.com|raw\.githubusercontent\.com)\//i.test(url)) {
      this.error = this.t.instant('pages.skills.importDialog.githubInvalid');
      return;
    }
    const paths = this.candidates.length ? [...this.selected] : undefined;
    if (paths && !paths.length) {
      this.error = this.t.instant('pages.skills.importDialog.pickSkills');
      return;
    }
    this.uploading = true;
    this.error = null;
    this.skills.importFromGithub(url, paths).subscribe({
      next: (res) => {
        this.uploading = false;
        if (res.imported.length === 1) {
          this.onImported(res.imported[0]);
          this.dialogRef.close();
        } else if (res.imported.length > 1) {
          this.onImportedMany(res.imported);
          this.dialogRef.close();
        } else {
          this.candidates = res.candidates;
          this.selected = new Set();
          if (!res.candidates.some((c) => c.importable)) {
            this.error = this.t.instant('pages.skills.importDialog.noneImportable');
          }
        }
      },
      error: (err) => {
        this.uploading = false;
        this.error = skillErrorText(err, this.t);
      },
    });
  }

  onUrlChange(): void {
    // a new link invalidates the list picked from the old one
    this.candidates = [];
    this.selected = new Set();
  }

  trackCandidate = (_: number, c: LibrarySkill) => c.path;

  upload(): void {
    if (!this.file) {
      this.error = this.t.instant('pages.skills.importDialog.pickFile');
      return;
    }
    this.uploading = true;
    this.error = null;
    this.skills.import(this.file).subscribe({
      next: (skill) => {
        this.uploading = false;
        this.onImported(skill);
        this.dialogRef.close();
      },
      error: (err) => {
        this.uploading = false;
        this.error = skillErrorText(err, this.t);
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
